// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Camera near/far planes fitted to the scene (frontend-r22.19).
 *
 * The planes used to be set once, when the first streamed page was framed,
 * from that page's bounds. A Morton-ordered cloud's first page is one corner
 * of the building, so the far plane ended a few metres past it: the rest of
 * the building streamed in behind the far plane, and dollying out cut the
 * whole cloud away. The scene now refits the planes every frame from the
 * bounding sphere of everything loaded and the camera's distance to it, which
 * covers bounds growth, dolly, orbit, focus, projection toggles and the
 * return from a panorama with one rule.
 *
 * Pure maths, so it is unit-tested without a WebGL context.
 */

import type { CameraProjection } from './cameraViews';

/** Closest a perspective near plane goes, metres: a wall the camera stands at stays drawn. */
export const MIN_NEAR = 0.01;

/**
 * Largest far/near ratio a perspective camera is given. Past it the depth
 * buffer runs out of precision; at 1e5 a camera inside a 1 km site still has
 * a 1 cm near plane.
 */
export const MAX_DEPTH_RATIO = 1e5;

/** Smallest sphere radius fitted, so an empty or single-point scene still gets a sane frustum. */
const MIN_RADIUS = 0.5;

export interface ClipPlanes {
  near: number;
  far: number;
}

/**
 * Near/far planes that keep a whole bounding sphere inside the frustum from
 * wherever the camera stands.
 *
 * @param distance Camera position to sphere centre, metres.
 * @param radius   Bounding-sphere radius of all loaded content, metres.
 * @param projection The active camera's projection.
 *
 * Perspective: far reaches the sphere's back with a margin. Near sits halfway
 * to the sphere's front when the camera is outside it (depth precision where
 * it is needed) and at {@link MIN_NEAR} when the camera is inside it, where
 * geometry can be arbitrarily close — floored so far/near stays within
 * {@link MAX_DEPTH_RATIO}.
 *
 * Orthographic: depth has no perspective division, so near may be negative —
 * the slab spans the whole sphere even when the camera has been dollied or
 * panned into it, and nothing behind the camera is cut.
 */
export function fitClipPlanes(distance: number, radius: number, projection: CameraProjection): ClipPlanes {
  const d = Number.isFinite(distance) ? Math.max(distance, 0) : 0;
  const r = Number.isFinite(radius) ? Math.max(radius, MIN_RADIUS) : MIN_RADIUS;
  const margin = r * 0.05 + 0.1;
  const far = d + r + margin;

  if (projection === 'orthographic') return { near: d - r - margin, far };

  const gap = d - r - margin;
  const near = Math.max(gap > 0 ? gap / 2 : MIN_NEAR, MIN_NEAR, far / MAX_DEPTH_RATIO);
  return { near, far: Math.max(far, near * 2) };
}

/**
 * Whether a camera's planes differ enough from @p next to be worth a
 * projection-matrix update (more than 1 % on either plane). Keeps the
 * per-frame refit from rebuilding the matrix on every frame of an idle view.
 */
export function clipPlanesChanged(current: ClipPlanes, next: ClipPlanes): boolean {
  const differs = (a: number, b: number) => Math.abs(a - b) > Math.max(Math.abs(b) * 0.01, 1e-4);
  return differs(current.near, next.near) || differs(current.far, next.far);
}

/**
 * Whether the automatic first framing should be redone because the loaded
 * bounds have grown: the first page frames a corner of the building, and
 * until the user takes the camera the view keeps up with the whole of it.
 * A 15 % growth threshold keeps the camera from twitching on every page.
 */
export function shouldRefollow(framedRadius: number, currentRadius: number): boolean {
  if (!Number.isFinite(currentRadius) || currentRadius <= 0) return false;
  if (!Number.isFinite(framedRadius) || framedRadius <= 0) return true;
  return currentRadius > framedRadius * 1.15;
}
